import React, { useState, useCallback, useContext } from 'react';
import { TranslationContext } from '../../contexts/TranslationContext';
import { NavPage } from '../../services/NavService';
import { State, useState2 } from '../../util/useState2';
import { usePromise } from '../EditorCommon/usePromise';
import { LoadSpinner } from '../LoadSpinner';
import { AdminNav } from './AdminNav/AdminNav';
import "./GenericEditor.scss"


interface GenericEditorProps<T extends { id: TKey }, TKey extends string | number> {
    /** Function to return a list of Ts. This will be called every time onChange() is called from editorFunc */
    getterFunc: () => Promise<T[]>,
    /** Function to get the displayName of a T (for displaying in the left toolbar) */
    nameFunc: (t: T) => React.ReactNode,
    /** Function to get an editor for a single T. The element should have a key that is based both on T.id and on updateCount */
    editorFunc: (t: T, onChange: () => void, updateCount: number) => React.ReactNode
    className?: string,
    navPage?: NavPage,
    /** If set, makes a new button in the bottom of the left toolbar that should create a new T */
    newHandler?: {
        /**Text to display on the new button */
        buttonName: string,
        /** Handler to create a new T with default values. */
        onClick: () => Promise<TKey | undefined>
    },
    /** If set, this element is placed in the left toolbox, below the main list */
    leftToolbox?: React.ReactNode,

    selectedId?: State<TKey | undefined>,
}

export function GenericEditor<T extends { id: TKey }, TKey extends string | number = number>(props: GenericEditorProps<T, TKey>) {
    const [updateCount, setUpdateCount] = useState(0);

    //use the provided selected id, if provided
    //call useState no matter what.
    const internalSelectedId = useState2<TKey | undefined>(undefined);
    const selectedId = props.selectedId ?? internalSelectedId;

    const tc = useContext(TranslationContext);
    const getterFunc = props.getterFunc;
    const itemResponse = usePromise(useCallback(async () => {
        const response = await getterFunc();
        return {
            items: response,
            updateCount,
        };
    },
        [updateCount, getterFunc]
    ));

    const selectedItem = itemResponse?.items.find(i => i.id === selectedId.value);

    const itemElements = itemResponse?.items.map(i => {
        const classes = i.id === selectedId.value
            ? "list-group-item active"
            : "list-group-item";
        return (
            <li key={i.id}
                className={classes}
                onClick={() => selectedId.set(i.id)}
            >
                {props.nameFunc(i)}
            </li>
        );
    });


    function onChange() {
        setUpdateCount(updateCount + 1);
    }

    async function onNew(itemCreator: () => Promise<TKey | undefined>) {
        try {
            selectedId.set(undefined);
            const newId = await itemCreator();
            selectedId.set(newId);
        }
        finally {
            onChange();
        }
    }

    return (
        <div className={`editor generic-editor ext-editor ${props.className ?? ''}`}>
            {props.navPage && <AdminNav page={props.navPage} />}

            <div className="left-toolbox">
                {itemResponse === undefined
                    ? <LoadSpinner />
                    : (
                        <>
                            <ul className="list-group">
                                {itemElements}
                                {props.newHandler && (
                                    <li className="list-group-item"
                                        onClick={() => onNew(props.newHandler!.onClick)}
                                    >
                                        {props.newHandler.buttonName}
                                    </li>

                                )}
                            </ul>

                            {props.leftToolbox}
                        </>
                    )
                }
            </div>

            <div className="main-editor">
                {selectedItem
                    ? props.editorFunc(selectedItem, onChange, itemResponse?.updateCount ?? -1)
                    : <span>{tc.translator("generic editor nothing selected", "Vælg et element fra listen til venstre.")}</span>
                }
            </div>
        </div>
    );
}
